import { apiClient } from '@/lib/api/client'

// ─── Types ──────────────────────────────────────────────
//
// Mirrors external_catalog_artists / external_catalog_artworks (migration 202610130001). Two
// questions are tracked separately and the UI must not merge them: `matchStatus` is "is this one
// of our artists", `reviewStatus` is "do we want this artist". An artist we already have needs
// no review; an artist we want is not yet linked to anyone.

export type MatchStatus = 'unmatched' | 'suggested' | 'linked' | 'rejected'
export type ReviewStatus = 'new' | 'shortlisted' | 'approved' | 'declined'
export type ArtistPriority = 'announce' | 'catalog'
export type IngestStatus =
  | 'new' | 'queued' | 'ingesting' | 'ingested' | 'failed' | 'skipped' | 'expired'

export interface ProviderStats {
  artists: number
  linked: number
  suggested: number
  shortlisted: number
  approved: number
  catalogArtworks: number
  lastSyncedAt: string | null
  artworks: {
    mirrored: number
    have: number
    missingForLinked: number
    queued: number
    failed: number
    expiredHeld: number
  }
}

export interface CatalogProvider {
  key: string
  label: string
  configured: boolean
  configHint: string | null
  /** False where this environment may not make billable print-file downloads. */
  downloadsEnabled: boolean
  stats: ProviderStats
}

export interface ExternalArtist {
  id: number
  provider: string
  externalId: string
  name: string
  username: string | null
  artistType: string | null
  url: string | null
  profilePicture: string | null
  country: string | null
  biography: string | null
  artworkCount: number | null
  artistId: number | null
  artistName?: string | null
  matchStatus: MatchStatus
  suggestedArtistId: number | null
  suggestedArtistName?: string | null
  matchScore: number | null
  reviewStatus: ReviewStatus
  priority: ArtistPriority | null
  notes: string | null
  agent: { score?: number; rationale?: string; tags?: string[] } | null
  artworksSyncedAt: string | null
  /** Artworks mirrored locally / of those, how many we hold. Both 0 until artworks are synced. */
  mirrored?: number
  have?: number
}

export interface ExternalArtwork {
  externalId: string
  externalArtistId: string | null
  artistName?: string | null
  title: string | null
  keywords: string | null
  artworkType: string | null
  category: string | null
  grade: number | null
  width: number | null
  height: number | null
  hasNudity: boolean | null
  thumbUrl: string | null
  previewUrl: string | null
  publishedAt: string | null
  sourceUrl: string
  artworkId?: number | null
  matchedBy?: string | null
  ingestStatus?: IngestStatus
  ingestError?: string | null
  expiredAt?: string | null
  /**
   * One of ours that this looks like, without being certain. While set, the artwork is neither
   * held nor missing and cannot be queued — someone has to say which it is.
   */
  candidateArtworkId?: number | null
}

export interface Coverage {
  mirrored: number
  have: number
  missing: number
  /** Look-alikes waiting for a person to say "same piece" or "different". */
  review: number
  queued: number
  failed: number
  expired: number
}

export type CoverageFilter = 'all' | 'missing' | 'review' | 'have'

export interface OurArtist {
  id: number
  artisticName: string
  email: string
  artworks?: number
}

export interface Pagination {
  total: number
  limit: number
  offset: number
  hasMore: boolean
}

export interface ArtistListParams {
  q?: string
  matchStatus?: MatchStatus
  reviewStatus?: ReviewStatus
  priority?: ArtistPriority
  minArtworks?: number
  sort?: 'artworks' | 'name' | 'recent' | 'recommended'
  limit?: number
  offset?: number
}

export interface SearchParams {
  q?: string
  artistId?: string
  order?: string
  artworkType?: string
  geometry?: string
  minGrade?: number
  page?: number
  perPage?: number
}

export interface ReviewPayload {
  reviewStatus?: ReviewStatus
  priority?: ArtistPriority | null
  notes?: string | null
}

// ─── API Client ─────────────────────────────────────────
//
// Staff surface (JWT + MANAGER). The vendor's API key stays on the server: the browser asks AMS
// to search and sync, and AMS talks to the vendor.

const BASE = '/api/external-catalogs'
const unwrap = <T>(response: { data: { data?: T } & T }): T => response.data?.data ?? response.data

export const externalCatalogApi = {
  getProviders: async (): Promise<{ providers: CatalogProvider[] }> =>
    unwrap(await apiClient.get(BASE)),

  getArtists: async (
    provider: string,
    params: ArtistListParams = {},
  ): Promise<{ artists: ExternalArtist[]; pagination: Pagination }> =>
    unwrap(await apiClient.get(`${BASE}/${provider}/artists`, { params })),

  getArtist: async (
    provider: string,
    externalId: string,
  ): Promise<{ artist: ExternalArtist; ours: OurArtist | null; coverage: Coverage }> =>
    unwrap(await apiClient.get(`${BASE}/${provider}/artists/${externalId}`)),

  getArtworks: async (
    provider: string,
    externalId: string,
    coverage: CoverageFilter = 'all',
  ): Promise<{ artworks: ExternalArtwork[]; pagination: Pagination }> =>
    unwrap(
      await apiClient.get(`${BASE}/${provider}/artists/${externalId}/artworks`, {
        params: { coverage, limit: 500 },
      }),
    ),

  search: async (
    provider: string,
    params: SearchParams,
  ): Promise<{ artworks: ExternalArtwork[]; page: number; perPage: number; hasMore: boolean }> =>
    unwrap(await apiClient.get(`${BASE}/${provider}/search`, { params })),

  findOurArtists: async (q: string): Promise<{ artists: OurArtist[] }> =>
    unwrap(await apiClient.get(`${BASE}/our-artists`, { params: { q } })),

  syncArtists: async (
    provider: string,
  ): Promise<{ artists: number; created: number; suggested: number }> =>
    unwrap(await apiClient.post(`${BASE}/${provider}/sync/artists`)),

  syncArtist: async (
    provider: string,
    externalId: string,
  ): Promise<{ artworks: number; created: number; matched: number; complete: boolean }> =>
    unwrap(await apiClient.post(`${BASE}/${provider}/artists/${externalId}/sync`)),

  link: async (
    provider: string,
    externalId: string,
    artistId: number,
  ): Promise<{ artist: ExternalArtist; matched: number }> =>
    unwrap(await apiClient.post(`${BASE}/${provider}/artists/${externalId}/link`, { artistId })),

  unlink: async (
    provider: string,
    externalId: string,
    reject = false,
  ): Promise<{ artist: ExternalArtist }> =>
    unwrap(
      await apiClient.delete(`${BASE}/${provider}/artists/${externalId}/link`, {
        params: reject ? { reject: 1 } : undefined,
      }),
    ),

  review: async (
    provider: string,
    externalId: string,
    payload: ReviewPayload,
  ): Promise<{ artist: ExternalArtist }> =>
    unwrap(await apiClient.patch(`${BASE}/${provider}/artists/${externalId}`, payload)),

  queue: async (
    provider: string,
    externalIds: string[],
  ): Promise<{ queued: string[]; requested: number; downloadsEnabled: boolean }> =>
    unwrap(await apiClient.post(`${BASE}/${provider}/artworks/queue`, { externalIds })),

  resolve: async (
    provider: string,
    externalId: string,
    held: boolean,
  ): Promise<{ externalId: string; artworkId: number | null }> =>
    unwrap(await apiClient.post(`${BASE}/${provider}/artworks/${externalId}/resolve`, { held })),

  unqueue: async (provider: string, externalIds: string[]): Promise<{ unqueued: string[] }> =>
    unwrap(await apiClient.post(`${BASE}/${provider}/artworks/unqueue`, { externalIds })),
}
